import React, {useContext, useEffect, useRef, useState} from 'react';
import * as Builder from "../../../builder";
import {ButtonBar} from "../../../builder";
import {Alert, Button} from "antd";
import {Translate} from "../../../components/util/translation.util";
import {CountryCodeEnum, RegExEnum, TranslationDomainEnum} from "../../../types/enums";
import {observer} from "mobx-react";
import {StoreContext} from "../../../store.context";
import {useNavigate} from "react-router-dom";
import {TextType} from "../../../builder/form/type/TextType";
import {EntityType} from "../../../builder/form/type/EntityType";
import {Constraint, EmailConstraint, MaxLengthConstraint, RegExpConstraint, RequiredConstraint} from "../../../builder/form/validation/constraints";
import {ChoiceType} from "../../../builder/form/type/ChoiceType";
import {useFormEventListener} from "../../../provider/event/event.context.provider";
import {EmailType} from "../../../builder/form/type/EmailType";
import {FormatHandler} from "../../../handler/format.handler";
import {OtherCompanyPrepareAddFormResponse, OtherCompanyPrepareEditFormResponse} from "../../../types/modules/otherCompanu/response";
import {OtherCompanyAddRequest, OtherCompanyRequestData} from "../../../types/modules/otherCompanu/request";
import {BaseAddResponse, BaseEditResponse} from "../../../types/response";

export const Form: React.FC<{
    onCancel?: () => void,
    onSave: (values: any) => Promise<BaseAddResponse|BaseEditResponse>,
    preparedData: OtherCompanyPrepareAddFormResponse | OtherCompanyPrepareEditFormResponse
}> = observer(({onCancel, onSave, preparedData}) => {
    const {appStore: {countries, findAddress}} = useContext(StoreContext);

    const navigate = useNavigate();
    const formReference = useRef() as any;

    //when you need to change form input element visibility while filling the form, we need to import useFormEventListener hook.
    const {onFormChange} = useFormEventListener();

    const [fetching, setFetching] = useState<boolean>(false)
    const [formErrors, setFormErrors] = useState<any>([]);
    const [addressDisabled, setAddressDisabled] = useState<boolean>(false)

    const [country, setCountry] = useState<string | undefined>();
    const [postalCodeConstraints, setPostalCodeConstraints] = useState<Constraint[]>([RequiredConstraint()]);
    const [phoneConstraints, setPhoneConstraints] = useState<Constraint[]>([ MaxLengthConstraint(20)]);

    const [address, setAddress] = useState<any>();
    const [addressFindLoading, setAddressFindLoading] = useState<boolean>();
    const [addressWarning, setAddressWarning] = useState<string | null>(null);

    const [showMonthlyOverviewEmail, setShowMonthlyOverviewEmail] = useState<boolean>(false);

    useEffect(() => {
        setFetching(false);
        //prefilling the form
        if (!preparedData.data) {
            //form validations will be automatically updated in "Form constraint validation" useEffect
            preparedData.data = {
                data: {
                    stopExcasso: false,
                    sendMonthlyOverview: false,
                    address: {
                        country: CountryCodeEnum.NETHERLANDS
                    }
                }
            } as OtherCompanyRequestData;
        }

        formReference.current?.setData({...preparedData.data,...preparedData.extraData});
        handleAddress(preparedData.data.data?.address, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const eventFn = (changedData: any, formData: any) => {
            if (!formData) return;

            setShowMonthlyOverviewEmail(formData?.data?.sendMonthlyOverview);
            setCountry(formData?.data?.address?.country);

            if (changedData?.data?.address) {
                setAddress(formData?.data?.address);
            }

            if (changedData?.data?.sendMonthlyOverview && formData?.data?.sendMonthlyOverview) {
                if (formData?.data?.contactData?.email && !formData?.data?.monthlyOverviewEmail) {
                    formReference.current?.setData({data: {monthlyOverviewEmail: formData?.data?.contactData?.email}})
                }
            }
        };

        //event listener for form
        onFormChange(eventFn);

        //manually trigger the form change event with route loader data
        eventFn({}, preparedData.data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //Form constraint validation, if country is changed on the UI, we handle country constraints
    useEffect(() => {
        if (country === CountryCodeEnum.NETHERLANDS) {
            setPostalCodeConstraints([
                RequiredConstraint(),
                RegExpConstraint(RegExEnum.DUTCH_POSTAL_CODE_REGEX, 'global.invalid_nl_postal_code'),
            ]);
        } else {
            setPostalCodeConstraints([
                RequiredConstraint(),
                RegExpConstraint(RegExEnum.POSTAL_CODE_REGEX, 'global.invalid_postal_code'),
            ]);
        }
    }, [country]);

    useEffect(() => {
        formReference.current.reValidateFields(['data.address.postalCode']);
    }, [postalCodeConstraints]);

    const onSubmit = (values: OtherCompanyAddRequest) => {
        setFetching(true);

        onSave(values)
                .then((data) => {
                    navigate(`/other_company/${data.id}/view`);
                })
                .catch(error => {
                    setFormErrors(error.response.data.errors);
                })
                .finally(() => {
                    setFetching(false);
                })
    }

    const handleAddress = (input: any, initial: boolean = false) => {
        if (input?.country === CountryCodeEnum.NETHERLANDS) {

            const postalCodeRegEx = new RegExp(RegExEnum.DUTCH_POSTAL_CODE_REGEX);
            const houseNumberRegEx = new RegExp(RegExEnum.HOUSE_NR_REGEX);

            if (postalCodeRegEx.test(input?.postalCode || "") && houseNumberRegEx.test(input?.houseNumber || "")) {
                setAddressFindLoading(true);
                findAddress(input).then(({data}) => {
                    if (data?.city || data?.street) {
                        if (!(initial && (data?.city !== input.city || data?.street !== input.street))) {
                            formReference.current?.setData({
                                data: {
                                    address: {
                                        city: data.city,
                                        street: data.street
                                    }
                                }
                            });
                        }

                        setAddressDisabled(true);
                        setAddressWarning(null);
                    } else {
                        setAddressDisabled(false);
                        setAddressWarning('global.message.address_not_found')
                    }
                }).catch(() => {
                    setAddressDisabled(false);
                    setAddressWarning('global.message.address_not_found')
                }).finally(() => {
                    setAddressFindLoading(false);
                })
            } else {
                setAddressDisabled(false);
                setAddressWarning(null);
            }
        } else {
            setAddressDisabled(false);
            setAddressWarning(null);
        }
    }

    useEffect(() => {
        handleAddress(address);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address?.postalCode, address?.houseNumber, address?.houseNumberAdditions, address?.country]);

    return (
            <>
                {addressWarning && (
                    <Alert
                        showIcon
                        closable
                        style={{marginBottom: "8px"}}
                        message={<Translate message={addressWarning}/>}
                        type={`warning`}
                    />
                )}

                <Builder.Form
                        translationDomain={TranslationDomainEnum.COMPANY_ADMINISTRATION}
                        formErrors={formErrors}
                        ref={formReference}
                        items={[
                            {
                                title: <Translate message={`otherCompany.title.company`}
                                                  domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>,
                                position: 'left',
                                inputs: [
                                    preparedData.extraData?.id ?
                                            {
                                                name: 'id',
                                                label: 'otherCompany.fields.relationalId',
                                                type: TextType,
                                                disabled: true,
                                            }
                                            : null,
                                    {
                                        name: 'data.name',
                                        label: 'otherCompany.fields.name',
                                        type: TextType,
                                        constraints: [
                                            RequiredConstraint(),
                                            MaxLengthConstraint(120)
                                        ]
                                    },
                                    preparedData.extraData?.id ?
                                            {
                                                name: 'externalId',
                                                label: 'otherCompany.fields.externalId',
                                                type: TextType,
                                                disabled: true,
                                            }
                                            : null,

                                ]
                            },
                            {
                                title: <Translate message={`otherCompany.title.contact_data`}
                                                  domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>,
                                position: 'left',
                                translationDomain: TranslationDomainEnum.COMPANY_ADMINISTRATION,
                                inputs: [
                                    {
                                        name: 'data.contactData.phone',
                                        label: 'companyContactData.fields.phone',
                                        type: TextType,
                                        constraints: phoneConstraints
                                    },
                                    {
                                        name: 'data.contactData.email',
                                        label: 'companyContactData.fields.email',
                                        type: EmailType,
                                        constraints: [
                                            MaxLengthConstraint(80),
                                        ]
                                    },
                                ]
                            },
                            {
                                title: <Translate message={`otherCompany.title.address`}
                                                  domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>,
                                position: 'left',
                                translationDomain: TranslationDomainEnum.COMPANY_ADMINISTRATION,
                                inputs: [
                                    {
                                        name: 'data.address.country',
                                        label: 'companyAddress.fields.country',
                                        type: EntityType,
                                        choices: countries,
                                        constraints: [
                                            RequiredConstraint(),
                                        ],
                                    },
                                    {
                                        name: 'data.address.postalCode',
                                        label: 'companyAddress.fields.postalCode',
                                        type: TextType,
                                        constraints: postalCodeConstraints
                                    },
                                    {
                                        name: 'data.address.houseNumber',
                                        label: 'companyAddress.fields.houseNumber',
                                        type: TextType,
                                        constraints: [
                                            RequiredConstraint(),
                                            RegExpConstraint(RegExEnum.HOUSE_NR_REGEX, 'global.invalid_house_number'),
                                            MaxLengthConstraint(15)
                                        ]
                                    },
                                    {
                                        name: 'data.address.houseNumberAdditions',
                                        label: 'companyAddress.fields.houseNumberAdditions',
                                        type: TextType,
                                        constraints: [
                                            MaxLengthConstraint(6)
                                        ]
                                    },
                                    {
                                        name: 'data.address.street',
                                        label: 'companyAddress.fields.street',
                                        type: TextType,
                                        disabled: addressDisabled,
                                        constraints: [
                                            RequiredConstraint(),
                                            MaxLengthConstraint(75)
                                        ],
                                        loading: addressFindLoading
                                    },
                                    {
                                        name: 'data.address.city',
                                        label: 'companyAddress.fields.city',
                                        disabled: addressDisabled,
                                        type: TextType,
                                        constraints: [
                                            RequiredConstraint(),
                                            MaxLengthConstraint(75)
                                        ],
                                        loading: addressFindLoading
                                    }
                                ]
                            },
                            {
                                title: <Translate message={`otherCompany.title.payment_information`}
                                                  domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>,
                                position: 'right',
                                inputs: [
                                    {
                                        name: 'data.bankData.iban',
                                        label: 'companyBankData.fields.iban',
                                        type: TextType,
                                        onInput: (event: any) => {
                                            event.target.value = FormatHandler.formatIban(event.target.value);
                                        },
                                        constraints: [
                                            MaxLengthConstraint(34)
                                        ]
                                    },
                                    {
                                        name: 'data.bankData.accountHolderName',
                                        label: 'companyBankData.fields.accountHolderName',
                                        type: TextType,
                                        constraints: [
                                            MaxLengthConstraint(70)
                                        ]
                                    },
                                    {
                                        name: 'data.stopExcasso',
                                        label: 'otherCompany.fields.stopExcasso',
                                        type: ChoiceType,
                                        choices: [{value: true, name: "global.yes"}, {value: false, name: "global.no"}],
                                        expanded: true,
                                        multiple: false,
                                        choiceTranslationDomain: TranslationDomainEnum.MESSAGES,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    },
                                    {
                                        name: 'data.sendMonthlyOverview',
                                        label: 'otherCompany.fields.sendMonthlyOverview',
                                        type: ChoiceType,
                                        choices: [{value: true, name: "global.yes"}, {value: false, name: "global.no"}],
                                        expanded: true,
                                        multiple: false,
                                        choiceTranslationDomain: TranslationDomainEnum.MESSAGES,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    },
                                    showMonthlyOverviewEmail ? {
                                        name: 'data.monthlyOverviewEmail',
                                        label: 'otherCompany.fields.monthlyOverviewEmail',
                                        type: EmailType,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    } : null,
                                ]
                            },
                            {
                                title: <Translate message={`otherCompany.title.settings`}
                                                  domain={TranslationDomainEnum.COMPANY_ADMINISTRATION}/>,
                                position: 'right',
                                inputs: [
                                    {
                                        name: 'data.disabled',
                                        label: 'otherCompany.fields.disabled',
                                        type: ChoiceType,
                                        expanded: true,
                                        choices: [{value: true, name: "global.not_active"}, {value: false, name: "global.active"}],
                                        choiceTranslationDomain: TranslationDomainEnum.MESSAGES,
                                        constraints: [
                                            RequiredConstraint(),
                                        ]
                                    },
                                ]
                            },


                        ]}
                        onFinish={(values: any) => {
                            onSubmit(values)
                        }}
                        initialValues={[]}
                />

                <ButtonBar>
                    <Button type="primary" loading={fetching} htmlType={`submit`} onClick={() => {
                        formReference.current?.submitForm()
                    }}><Translate message={`global.button.save`}/></Button>

                    <Button type="default" className={`ant-btn-secondary`} onClick={() => {
                        onCancel && onCancel()
                    }}><Translate message={`global.button.back_to_list`}/></Button>
                </ButtonBar>
            </>
    )
})